import {IconButton} from '@dropbox/dig-components/buttons';
import {Checkbox} from '@dropbox/dig-components/controls';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronRightLine, MoreHorizontalLine} from '@dropbox/dig-icons/assets';
import {t} from 'i18next';
import {type MouseEvent} from 'react';

import {
  CalendarPeopleFilterState,
  CalendarPeopleGroup,
  isCalendarPeopleGroupEnabled,
  isPeopleFilterAtDefaults,
} from './calendarPeopleGroups';
import {CalendarTileView} from './CalendarViewToggle';
import {WeekStart} from './WeekStartToggle';

/** Keep the menu above dashboard chrome (sticky search is z-index 4). */
const CALENDAR_MENU_Z_INDEX = 20;

const submenuTrigger = (label: string, value: string) => (
  <Box
    display="flex"
    alignItems="center"
    justifyContent="space-between"
    style={{width: '100%', minWidth: 0, minHeight: 24, gap: 12}}
  >
    <Box style={{flex: 1, minWidth: 0}}>
      <Text>{label}</Text>
    </Box>
    <Box display="flex" alignItems="center" style={{flexShrink: 0}}>
      <Text size="small" color="subtle">
        {value}
      </Text>
    </Box>
  </Box>
);

const submenuChevron = <UIIcon src={ChevronRightLine} className={atoms({color: 'Text Subtle'})} />;

export const CalendarTileMenu = ({
  view,
  onViewChange,
  weekStartsOn,
  onWeekStartChange,
  peopleFilter,
  onTogglePeopleGroup,
  availablePeopleGroups,
  onMenuOpen,
}: {
  view: CalendarTileView;
  onViewChange: (view: CalendarTileView) => void;
  weekStartsOn: WeekStart;
  onWeekStartChange: (weekStartsOn: WeekStart) => void;
  peopleFilter: CalendarPeopleFilterState;
  onTogglePeopleGroup: (group: CalendarPeopleGroup) => void;
  availablePeopleGroups: CalendarPeopleGroup[];
  onMenuOpen?: () => void;
}) => {
  const viewLabel =
    view === 'calendar' ? t('dashboard_calendar_view_calendar') : t('dashboard_calendar_view_list');

  const weekStartLabel =
    weekStartsOn === 1 ? t('dashboard_week_start_monday') : t('dashboard_week_start_sunday');

  const peopleLabel = isPeopleFilterAtDefaults(peopleFilter, availablePeopleGroups)
    ? t('dashboard_calendar_people_default')
    : t('dashboard_calendar_people_count', {
        enabled: availablePeopleGroups.filter((group) =>
          isCalendarPeopleGroupEnabled(peopleFilter, group)
        ).length,
        total: availablePeopleGroups.length,
      });

  return (
    <Menu.Wrapper closeOnSelection={false} shouldPropagateClickOutsideMouseEvents>
      {({getContentProps, getTriggerProps}) => {
        const triggerProps = getTriggerProps();
        return (
          <>
            <IconButton
              {...triggerProps}
              variant="borderless"
              size="small"
              aria-label={t('dashboard_calendar_menu_aria').toString()}
              onClick={(e: MouseEvent<HTMLButtonElement>) => {
                triggerProps.onClick?.(e);
                onMenuOpen?.();
              }}
              onMouseDown={(e: MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
              }}
            >
              <UIIcon src={MoreHorizontalLine} className={atoms({color: 'Text Subtle'})} />
            </IconButton>
            <Menu.Content
              {...getContentProps()}
              minWidth="220px"
              placement="top"
              positioningStrategy="fixed"
              style={{zIndex: CALENDAR_MENU_Z_INDEX}}
            >
              <Menu.Segment>
                <Menu.Submenu
                  withTriggerContent={submenuTrigger(
                    t('dashboard_calendar_menu_view').toString(),
                    viewLabel.toString()
                  )}
                  withRightAccessory={submenuChevron}
                  closeOnSelection={false}
                  contentProps={{style: {zIndex: CALENDAR_MENU_Z_INDEX}}}
                >
                  <Menu.Segment>
                    <Menu.SelectItem
                      selected={view === 'calendar'}
                      onClick={() => onViewChange('calendar')}
                    >
                      {t('dashboard_calendar_view_calendar')}
                    </Menu.SelectItem>
                    <Menu.SelectItem
                      selected={view === 'list'}
                      onClick={() => onViewChange('list')}
                    >
                      {t('dashboard_calendar_view_list')}
                    </Menu.SelectItem>
                  </Menu.Segment>
                </Menu.Submenu>
                <Menu.Submenu
                  withTriggerContent={submenuTrigger(
                    t('dashboard_calendar_menu_week_start').toString(),
                    weekStartLabel.toString()
                  )}
                  withRightAccessory={submenuChevron}
                  closeOnSelection={false}
                  contentProps={{style: {zIndex: CALENDAR_MENU_Z_INDEX}}}
                >
                  <Menu.Segment>
                    <Menu.SelectItem
                      selected={weekStartsOn === 0}
                      onClick={() => onWeekStartChange(0)}
                    >
                      {t('dashboard_week_start_sunday')}
                    </Menu.SelectItem>
                    <Menu.SelectItem
                      selected={weekStartsOn === 1}
                      onClick={() => onWeekStartChange(1)}
                    >
                      {t('dashboard_week_start_monday')}
                    </Menu.SelectItem>
                  </Menu.Segment>
                </Menu.Submenu>
                {availablePeopleGroups.length > 1 ? (
                  <Menu.Submenu
                    withTriggerContent={submenuTrigger(
                      t('dashboard_calendar_menu_people').toString(),
                      peopleLabel.toString()
                    )}
                    withRightAccessory={submenuChevron}
                    closeOnSelection={false}
                    contentProps={{style: {zIndex: CALENDAR_MENU_Z_INDEX}}}
                  >
                    <Menu.Segment>
                      {availablePeopleGroups.map((group) => {
                        const isEnabled = isCalendarPeopleGroupEnabled(peopleFilter, group);
                        return (
                          <Menu.ActionItem
                            key={group}
                            onClick={() => onTogglePeopleGroup(group)}
                            withLeftAccessory={
                              <Checkbox checked={isEnabled} readOnly tabIndex={-1} />
                            }
                          >
                            {t(`dashboard_calendar_people_${group}`)}
                          </Menu.ActionItem>
                        );
                      })}
                    </Menu.Segment>
                  </Menu.Submenu>
                ) : null}
              </Menu.Segment>
            </Menu.Content>
          </>
        );
      }}
    </Menu.Wrapper>
  );
};
